// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.DashboardTile.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { untracked, observable, computed, action, toJS } from 'mobx';
import dashboardConstants from 'logic/data/Izenda.DashboardConstants';
import ReportPartDetails from 'logic/models/Izenda.ReportPartDetails';
import objectStateConstants from 'logic/data/Izenda.ObjectStateConstants';
import tracker from 'share/tracking/Izenda.Tracker';
import DashboardPartFilterField from './Izenda.DashboardPartFilterField';
import FilterDetailsConfig from '../Izenda.FilterDetailsConfig';
import TitleModel from './Izenda.TitleModel';
import UIConstants from 'logic/data/Izenda.UIConstants';
import DashboardTextTileToolbarConstants from 'logic/data/Izenda.DashboardTextTileToolbarConstants';
import FilterDetails from 'logic/models/Izenda.FilterDetails';
import filterConstants, {
  OPERATOR_NAMES
} from 'logic/data/Izenda.FilterDetailsConfigConstants';
import {
  split,
  includes,
  reduce,
  get,
  getOr,
  set,
  uniqueId,
  partial,
  compact,
  join,
  flow,
  filter,
  map,
  sortBy,
  negate,
  isNil,
  isBoolean,
  trim,
  invokeMap,
  forEach,
  cloneDeep,
  isNaN,
  omit,
  clone,
  isEmpty,
  values,
  find,
  some
} from 'lodash/fp';

const BUTTON_ALIGN_VERTICAL = DashboardTextTileToolbarConstants.BUTTON_ALIGN_VERTICAL;
const ZERO = 0;
function getFilterInfo(keyPattern, keyLookupPattern) {
  return reduce(
    (acc, filter) => {
      const position = get('configField.fieldDetails.position', filter);

      if (!position) {
        return acc;
      }

      const value = filter.value;

      let overideFilterValue = set(String.format(keyPattern, position), value, acc);
      return keyLookupPattern
        ? set(
            String.format(keyLookupPattern, position),
            get('configField.fieldDetails.useLookup', filter),
            overideFilterValue
          )
        : overideFilterValue;
    },
    {},
    this.filters
  );
}

@tracker
class DashboardTile {
  @observable id: string;
  @observable dashboardId: ?string;
  @observable positionX: number;
  @observable positionY: number;
  @observable width: number;
  @observable height: number;
  @observable title: string;
  @observable editingTitle: string;
  @observable isBackSide: boolean;
  @observable filterDescription: any;
  @observable isFullScreenMode: boolean;
  @observable numberOfRecord: number;
  @observable state: number;
  @observable filters: Array<any>;
  @observable type: any;
  @observable contentTitle: string;
  @observable contentDescription: any;
  @observable contentFromPreset: any;
  @observable bodyContent: any;
  @observable reportId: ?string;
  @observable reportPartId: ?string;
  @observable report: any;
  @observable reportPart: any;
  @observable disableViewReport: any;
  @observable disableViewReportMessage: ?string;
  @observable disableViewReportMessageKey: ?string;
  @observable filterRequired: Array<any>;

  reportDataSource: Array<any> = [];
  filterInfoForReport: Array<any>;
  filterInfoForSubReport: Array<any>;

  constructor(data: any) {
    this.reportDataSource = [];
    this.id = uniqueId('dashboardTile_');
    this.dashboardId = null;
    this.positionX = 0;
    this.positionY = 0;
    this.width = 0;
    this.height = 0;
    this.title = '';
    this.editingTitle = '';
    this.isBackSide = false;
    this.filterDescription = null;
    this.isFullScreenMode = false;
    this.numberOfRecord = -1;
    this.state = objectStateConstants.INSERT;
    this.filters = [];
    this.type = null;
    this.contentTitle = new TitleModel();
    this.contentDescription = new TitleModel();
    this.contentFromPreset = false;
    this.bodyContent = {
      text: '',
      config: {
        fontFamily: UIConstants.defaultFontFamily,
        fontSize: UIConstants.defaultFontSize,
        bold: false,
        italic: false,
        underline: false,
        strikethrough: false,
        textColor: '',
        backgroundColor: '',
        alignleft: false,
        aligncenter: false,
        alignright: false,
        alignjustify: false,
        bullet: '',
        numbered: '',
        alignTop: false,
        alignMiddle: false,
        alignBottom: false
      }
    };
    this.reportId = null;
    this.reportPartId = null;
    this.report = null;
    this.reportPart = null;
    this.disableViewReport = false;
    this.disableViewReportMessage = null;
    this.disableViewReportMessageKey = null;
    this.originalPosition = null;

    // for printing
    this.filterInfoForReport = partial(getFilterInfo, ['p{0}value']);
    // for open sub report
    this.filterInfoForSubReport = partial(getFilterInfo, ['mp{0}value', 'mp{0}lookup']);
    data && this.populateData(data);
  }

  @computed
  get isNew(): boolean {
    return this.state === objectStateConstants.INSERT;
  }

  @computed
  get isDelete(): boolean {
    return this.state === objectStateConstants.DELETE;
  }

  @computed
  get isFullSize(): boolean {
    return this.isBackSide;
  }

  @computed
  get getDefaultName(): string {
    if (this.type === dashboardConstants.TILE_TYPE.text.type) {
      return dashboardConstants.TILE_TYPE.text.type;
    }

    const nameArray = compact([
      get('categoryName', this.report) || dashboardConstants.UNCATEGORIZED,
      get('subCategoryName', this.report),
      get('name', this.report),
      get('headerTitle', this.reportPart)
    ]);

    return join('/', nameArray);
  }

  @computed
  get typeConfig(): any {
    return flow(
      values,
      find({
        type: this.type
      })
    )(dashboardConstants.TILE_TYPE);
  }

  @computed
  get isReportPartType(): boolean {
    return this.type === dashboardConstants.TILE_TYPE.reportPart.type;
  }

  @computed
  get isTextType(): boolean {
    return this.type === dashboardConstants.TILE_TYPE.text.type;
  }

  @computed
  get numberOfRecordDisplayValue(): number {
    return this.numberOfRecord < ZERO ? '' : this.numberOfRecord;
  }

  @computed
  get showNumberOfRecord(): boolean {
    return this.isReportPartType;
  }

  @computed
  get showRefeshButton(): boolean {
    return this.isReportPartType && !this.isBackSide;
  }

  // for display filter setting value
  @computed
  get individualConfigFieldList(): Array<any> {
    return flow(
      filter({
        isCommon: false
      }),
      map('configField')
    )(this.displayFilters);
  }

  // for display filter description
  @computed
  get fullConfigFieldList(): Array<any> {
    return flow(
      sortBy([negate(get('isCommon')), get('configField.fieldDetails.position')]),
      map('configField')
    )(this.displayFilters);
  }

  @computed
  get displayFilters(): Array<any> {
    return filter(
      filter =>
        !isNil(filter.filterField) &&
        filter.filterField.dataType != filterConstants.EXCLUDE_FILTER_DATA_TYPE.REF_CURSOR &&
        filter.filterField.dataType != filterConstants.EXCLUDE_FILTER_DATA_TYPE.SYS_REFCURSOR,
      this.filters
    );
  }

  /**
   * Get list required filter with empty value
   * @returns {Array}
   */
  emptyVisibleRequiredFilter(): Array<any> {
    // Using action for don't allow rerender when filterItem.value change.
    return untracked(() =>
      filter(filterItem => {
        const { filterField, value } = filterItem;

        const isEmptyValue =
          (isEmpty(trim(value)) || includes('', split(';#', value))) && isEmpty(trim(filterField.value));
        const operatorName = filterField.operatorName;
        const emptyValue =
          !filterField.isParameter &&
          filterField.required &&
          isEmptyValue &&
          operatorName &&
          !includes(operatorName, OPERATOR_NAMES);
        return emptyValue && filterField.visible;
      }, this.filters)
    );
  }

  /**
   * Check Dirty for Dashboard Tile
   * @returns {boolean}
   */
  checkDirty(): boolean {
    return this.isDirty || some('isDirty', this.filters);
  }

  /**
   * Clean Dirty for Dashboard Tile
   * @param {type} )
   */
  @action
  cleanDirty(): void {
    // Reset dirty dashboard tile
    this.resetDirty();

    // Reset dirty dashboard filter
    forEach(filter => {
      filter.resetDirty();
    }, this.filters);
  }

  /**
   * initial data for this object
   * @method populateData
   * @param  {any}     data Data from api/ preset
   */
  @action
  populateData(data: any): void {
    data.id && (this.id = data.id);
    this.dashboardId = data.dashboardId;
    this.positionX = data.positionX;
    this.positionY = data.positionY;
    this.filters = map(item => {
      item = cloneDeep(toJS(item));
      if (item.filterField) {
        item.filterField.value = item.value;
        item.filterField.uniqueId = uniqueId();
        item.filterField = new FilterDetails(item.filterField);
      }
      return new DashboardPartFilterField(item);
    }, data.filters);
    this.state = data.state;
    this.editingTitle = this.title = data.title;
    this.filterDescription = data.filterDescription;
    this.width = data.width;
    this.height = data.height;
    this.type = data.type;
    this.reportId = data.reportId;
    this.reportPartId = data.reportPartId;
    this.numberOfRecord = data.numberOfRecord;
    this.contentTitle.populateData(get('dashboardPartContent.contentTitle', data) || data.contentTitle);
    this.contentDescription.populateData(
      get('dashboardPartContent.contentDescription', data) || data.contentDescription
    );
    this.contentFromPreset = get('dashboardPartContent.contentFromPreset', data) || data.contentFromPreset;
    if (this.isTextType) {
      var textTypeContent = cloneDeep(
        toJS(get('dashboardPartContent.textTypeContent', data) || get('bodyContent.text', data))
      );

      this.bodyContent.text = textTypeContent || '';
    }
    this.reportDataSource = data.reportDataSource;

    this.cleanDirty();
  }

  /**
   * set report part position
   * @param {Object} position object
   */
  @action
  setPosition(position: any): void {
    this.positionX = position.positionX;
    this.positionY = position.positionY;
    this.width = position.width;
    this.height = position.height;

    this.markDirty();
  }

  /**
   * set original position for report part
   * @param {Object} position object
   */
  setOriginalPosition(position: any) {
    this.originalPosition = {
      positionX: position.positionX,
      positionY: position.positionY,
      width: position.width,
      height: position.height
    };
  }
  /**
   * select type for dashboard part
   * @method setType
   * @param  {any} typeConfig [description]
   * @param  {any} report     [description]
   * @param  {any} reportPart [description]
   */
  @action
  setType(typeConfig: any, reportPart: any, report: any) {
    this.type = typeConfig.type;
    if (reportPart) {
      this.reportId = reportPart.reportId;
      this.reportPartId = reportPart.id;
      this.setNumberOfRecord(reportPart.numberOfRecord);
      this.readReportInfo(report, reportPart);
      this.buildFilters(report);
    }
    this.markDirty();
  }

  /**
   * Calculate filter list.
   * @param {any} report
   */
  @action
  buildFilters(report: any) {
    this.filters = map(filterField => {
      return new DashboardPartFilterField({
        filterFieldId: filterField.id,
        value: filterField.value,
        operatorId: filterField.operatorId,
        isCommon: false,
        dashboardPartId: (!this.isNew && this.id) || null,
        filterField: new FilterDetails(filterField),
        displayName: filterField.alias
      });
    }, getOr([], 'reportFilter.filterFields', report));
  }

  /**
   * set number of record
   * @method Set number of record
   * @param  {numRecord} numRecord New Number of record
   */
  @action
  setNumberOfRecord(numRecord: number) {
    const number = parseInt(numRecord, 10);

    if (isNaN(number)) {
      this.numberOfRecord = -1;
    } else {
      this.numberOfRecord = number <= ZERO ? ZERO : number;
    }
    this.markDirty();
  }

  /**
   * Get data by key
   * @param {string} key
   */
  getDataByKey(key: string) {
    return this[key];
  }

  /**
   * Read report information
   * @param {any} reportDef
   * @param {any} reportPartDef
   */
  @action
  readReportInfo(reportDef: any, reportPartDef: any) {
    const detail = get('detail', reportDef);
    this.report = reportDef;
    if (this.reportPart) {
      this.reportPart.readData(reportPartDef, null, reportDef.accessPriority);
    } else {
      this.reportPart = new ReportPartDetails(reportPartDef, null, reportDef.accessPriority);
    }
    if (!isNil(detail)) {
      this.disableViewReport = true;
      this.disableViewReportMessage = detail.message;
      this.disableViewReportMessageKey = detail.messageKey;
    }
  }

  /**
   * Toggle side mode
   * @param {boolean} isBackSide
   */
  @action
  toggleSide(isBackSide: boolean) {
    this.isBackSide = isBoolean(isBackSide) ? isBackSide : !this.isBackSide;
  }

  /**
   * set name of dashboard part
   * @method setName
   * @param  {string} newName New Name
   */
  @action
  setTitle(newName: string) {
    this.editingTitle = this.title = trim(newName);
    this.markDirty();
  }

  /**
   * update editing title
   * @method setEditingTitle
   * @param  {string} newName
   */
  @action
  setEditingTitle(newName: string) {
    this.editingTitle = newName;
    this.markDirty();
  }

  /**
   * Update Text of Title
   * @param {string} text
   */
  @action
  updateContentTitle(text: string) {
    this.contentTitle.updateText(text);
    this.markDirty();
  }

  /**
   * Update Text of Description
   * @param {string} text
   */
  @action
  updateContentDescription(text: string) {
    this.contentDescription.updateText(text);
    this.markDirty();
  }

  /**
   * Update font settings of Title's text
   * @param {any} settings
   */
  @action
  updateContentTitleSettings(settings: any) {
    this.contentTitle.updateSettings(settings);
    this.markDirty();
  }

  /**
   * Update font setting of Description's text
   * @param {any} setting
   */
  @action
  updateContentDescriptionSettings(settings: any) {
    this.contentDescription.updateSettings(settings);
    this.markDirty();
  }

  /**
   * toggle fullscreen mode for dashboard tile
   */
  @action
  toggleFullScreenMode() {
    this.isFullScreenMode = !this.isFullScreenMode;
  }

  /**
   * Get Body content config
   */
  getBodyConfig() {
    return get('config', this.bodyContent);
  }

  /**
   * Get Body content text
   * @returns {string}
   */
  getBodyText() {
    return get('text', this.bodyContent);
  }

  /**
   * Set vertical align base on value (top|middle|bottom)
   * @param {string} value
   */
  @action
  setVerticalAlignConfig(value: string) {
    const config = this.bodyContent.config;
    const keys = Object.keys(BUTTON_ALIGN_VERTICAL);

    keys.forEach(key => {
      config[key] = value === key;
    });

    this.markDirty();
  }

  /**
   * Prepare data for save
   */
  prepareDataForSave() {
    const returnObject = omit(
      [
        'report',
        'reportPart',
        'id',
        'contentTitle',
        'contentDescription',
        'filters',
        'contentFromPreset',
        'editingTitle'
      ],
      this
    );

    if (this.state !== objectStateConstants.INSERT) {
      returnObject.id = this.id;
    } else {
      returnObject.id = null;
    }

    if (this.isDirty && this.state === objectStateConstants.NONE) {
      returnObject.state = objectStateConstants.UPDATE;
    }

    returnObject.reportId = this.reportId;
    returnObject.reportPartId = this.reportPartId;
    returnObject.dashboardPartContent = {
      contentTitle: this.contentTitle,
      contentDescription: this.contentDescription,
      contentFromPreset: this.contentFromPreset,
      textTypeContent: this.bodyContent.text
    };

    returnObject.filters = invokeMap('buildDataForSaving', this.filters);

    return returnObject;
  }

  /**
   * delete this dashboard tile
   * @method delete
   */
  @action
  delete() {
    this.state = objectStateConstants.DELETE;
    this.markDirty();
  }

  /**
   * Build config field list for this dashboard tile
   * @method buildConfigFieldList
   * @param {any} operatorGroup Result data from api
   * @param {Function} markUpdateResultFunc
   */
  @action
  buildConfigFieldList(operatorGroup: any, markUpdateResultFunc: Function) {
    const dashboardTile = this;

    forEach(item => {
      const filterDetailsConfig = new FilterDetailsConfig(item.filterField, markUpdateResultFunc);

      filterDetailsConfig.commonData.operatorGroup = clone(operatorGroup);
      filterDetailsConfig.value = item.value;

      filterDetailsConfig.setFilterValue = function(filterValue) {
        FilterDetailsConfig.prototype.setFilterValue.call(filterDetailsConfig, filterValue);
        item.setFilterValue(this.fieldDetails.value);
        item.setOperatorId(this.fieldDetails.operatorId);
        item.configField.value = this.fieldDetails.value;
        item.operatorId = this.fieldDetails.operatorId;
        dashboardTile.markDirty();
      };

      item.configField = filterDetailsConfig;
    }, this.filters);
  }

  /**
   * Calculate width, height for container
   * @param  {number} baseWidth
   */
  getRawContainerSize(baseWidth: number) {
    const autoSize =
      !isNil(this.type) && this.type !== dashboardConstants.TILE_TYPE.text.type
        ? getOr(false, 'autoSize', this.reportPart)
        : false;

    return autoSize
      ? {}
      : {
          height: baseWidth * this.height,
          width: baseWidth * this.width
        };
  }

  /**
   * Calculate width height in px for container
   * @param  {number} baseWidth
   */
  getContainerSize(baseWidth: number) {
    const rawSize = this.getRawContainerSize(baseWidth);

    return isEmpty(rawSize)
      ? {}
      : {
          height: rawSize.height + 'px',
          width: rawSize.width + 'px'
        };
  }

  /**
   * Calculate width height in px for report part.
   * Minus size of information session.
   * @param  {number} baseWidth
   * @param  {any} infoSize
   */
  getReportPartSize = function(baseWidth: number, infoSize: any) {
    const rawSize = this.getRawContainerSize(baseWidth);

    return isEmpty(rawSize)
      ? {}
      : {
          height: rawSize.height - infoSize.height + 'px',
          width: rawSize.width - infoSize.width + 'px'
        };
  };
}

export default DashboardTile;



// WEBPACK FOOTER //
// ./ui/logic/models/Dashboard/Izenda.DashboardTile.js